import "@/App.css";
import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

import MainLayout from "@/components/layout/MainLayout";
import ProtectedRoute from "@/components/layout/ProtectedRoute";
import RequireAuth from "@/components/layout/RequireAuth";

import Home from "@/pages/Home";
import Listings from "@/pages/Listings";
import MapSearch from "@/pages/MapSearch";
import ListingDetail from "@/pages/ListingDetail";
import AdvertiseHub from "@/pages/advertise/Hub";
import AgentsLanding from "@/pages/advertise/agents/AgentsLanding";
import AgentsPlan from "@/pages/advertise/agents/AgentsPlan";
import AgentsProfile from "@/pages/advertise/agents/AgentsProfile";
import AgentsGoals from "@/pages/advertise/agents/AgentsGoals";
import AgentsSchedule from "@/pages/advertise/agents/AgentsSchedule";
import AgentsReview from "@/pages/advertise/agents/AgentsReview";
import AgentsThankYou from "@/pages/advertise/agents/AgentsThankYou";
import SellersLanding from "@/pages/advertise/sellers/SellersLanding";
import SellersAddress from "@/pages/advertise/sellers/SellersAddress";
import SellersVerify from "@/pages/advertise/sellers/SellersVerify";
import SellersDetails from "@/pages/advertise/sellers/SellersDetails";
import SellersContact from "@/pages/advertise/sellers/SellersContact";
import SellersReview from "@/pages/advertise/sellers/SellersReview";
import SellersCheckout from "@/pages/advertise/sellers/SellersCheckout";
import SellersThankYou from "@/pages/advertise/sellers/SellersThankYou";
import BuildersLanding from "@/pages/advertise/builders/BuildersLanding";
import BuildersCompany from "@/pages/advertise/builders/BuildersCompany";
import BuildersPortfolio from "@/pages/advertise/builders/BuildersPortfolio";
import BuildersContact from "@/pages/advertise/builders/BuildersContact";
import BuildersSchedule from "@/pages/advertise/builders/BuildersSchedule";
import BuildersReview from "@/pages/advertise/builders/BuildersReview";
import BuildersThankYou from "@/pages/advertise/builders/BuildersThankYou";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Help from "@/pages/Help";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import Cookies from "@/pages/Cookies";
import DeleteAccount from "@/pages/DeleteAccount";
import Agents from "@/pages/Agents";
import AgentProfile from "@/pages/AgentProfile";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import VerifyEmail from "@/pages/VerifyEmail";
import NotFound from "@/pages/NotFound";


import DashboardLayout from "@/pages/dashboard/DashboardLayout";
import Overview from "@/pages/dashboard/Overview";
import SavedHomes from "@/pages/dashboard/SavedHomes";
import SavedSearches from "@/pages/dashboard/SavedSearches";
import Alerts from "@/pages/dashboard/Alerts";
import Notifications from "@/pages/dashboard/Notifications";
import MyListings from "@/pages/dashboard/MyListings";
import Messages from "@/pages/dashboard/Messages";
import Billing from "@/pages/dashboard/Billing";
import Profile from "@/pages/dashboard/Profile";
import BillingSuccess from "@/pages/dashboard/BillingSuccess";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";

import { useAuth } from "@/store/useAuth";
import { useFavorites } from "@/store/useFavorites";
import { shouldSkipPanelRedirect, redirectToPanel } from "@/utils/panelRedirect";

function App() {
  const refreshMe = useAuth((s) => s.refreshMe);
  const bootstrapping = useAuth((s) => s.bootstrapping);
  const isAuthenticated = useAuth((s) => s.isAuthenticated);
  const hydrateFavorites = useFavorites((s) => s.hydrate);

  useEffect(() => {
    refreshMe();
  }, [refreshMe]);

  useEffect(() => {
    if (!bootstrapping && isAuthenticated) {
      hydrateFavorites();
    }
  }, [bootstrapping, isAuthenticated, hydrateFavorites]);

  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    if (bootstrapping) return;
    if (!isAuthenticated) return;
    const path = window.location.pathname;
    if (shouldSkipPanelRedirect(path)) return;
    if (redirectToPanel()) setRedirecting(true);
  }, [bootstrapping, isAuthenticated]);

  if (bootstrapping || redirecting) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-slate-200 border-t-blue-600" />
      </div>
    );
  }

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route element={<MainLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/listings" element={<Listings />} />
            <Route path="/map" element={<MapSearch />} />
            <Route path="/listing/:id" element={<ListingDetail />} />
            <Route path="/advertise" element={<AdvertiseHub />} />
            <Route path="/advertise/agents" element={<AgentsLanding />} />
            <Route path="/advertise/agents/plan" element={<AgentsPlan />} />
            <Route path="/advertise/agents/profile" element={<AgentsProfile />} />
            <Route path="/advertise/agents/goals" element={<AgentsGoals />} />
            <Route path="/advertise/agents/schedule" element={<AgentsSchedule />} />
            <Route path="/advertise/agents/review" element={<AgentsReview />} />
            <Route path="/advertise/agents/thank-you" element={<AgentsThankYou />} />
            <Route path="/advertise/sellers" element={<SellersLanding />} />
            <Route path="/advertise/sellers/address" element={<SellersAddress />} />
            <Route path="/advertise/sellers/verify" element={<SellersVerify />} />
            <Route path="/advertise/sellers/details" element={<SellersDetails />} />
            <Route path="/advertise/sellers/contact" element={<SellersContact />} />
            <Route path="/advertise/sellers/review" element={<SellersReview />} />
            <Route path="/advertise/sellers/checkout" element={<SellersCheckout />} />
            <Route path="/advertise/sellers/thank-you" element={<SellersThankYou />} />
            <Route path="/advertise/builders" element={<BuildersLanding />} />
            <Route path="/advertise/builders/company" element={<BuildersCompany />} />
            <Route path="/advertise/builders/portfolio" element={<BuildersPortfolio />} />
            <Route path="/advertise/builders/contact" element={<BuildersContact />} />
            <Route path="/advertise/builders/schedule" element={<BuildersSchedule />} />
            <Route path="/advertise/builders/review" element={<BuildersReview />} />
            <Route path="/advertise/builders/thank-you" element={<BuildersThankYou />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/help" element={<Help />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/cookies" element={<Cookies />} />
            <Route path="/delete-account" element={<DeleteAccount />} />
            <Route path="/agents" element={<RequireAuth><Agents /></RequireAuth>} />

            <Route path="/agents/:ref" element={<RequireAuth><AgentProfile /></RequireAuth>} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/auth/verify/:token" element={<VerifyEmail />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />

            {/* Protected dashboard */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <DashboardLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Overview />} />
              <Route path="saved-homes" element={<SavedHomes />} />
              <Route path="saved-searches" element={<SavedSearches />} />
              <Route path="alerts" element={<Alerts />} />
              <Route path="notifications" element={<Notifications />} />
              <Route path="my-listings" element={<MyListings />} />
              <Route path="messages" element={<Messages />} />
              <Route path="billing" element={<Billing />} />
              <Route path="profile" element={<Profile />} />
              <Route path="billing/success" element={<BillingSuccess />} />
            </Route>

            <Route path="*" element={<NotFound />} />
          </Route>
        </Routes>
      </BrowserRouter>
      <Toaster position="top-center" richColors closeButton />
    </div>
  );
}

export default App;
